import { LinearProgress, makeStyles, Typography } from '@material-ui/core';
import { ChevronRight } from '@material-ui/icons';
import BoltIcon from '@material-ui/icons/OfflineBolt';
import ArrowCircleUpOutlinedIcon from '@src/resources/icons/ArrowCircleUpOutlinedIcon';
import { urlAgentConsole } from '@src/utils/adminRoutes';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export type AppUpdateProps = {
  version: string;
  onUpdate: () => void;
};

const DEFAULT_TIMEOUT = 20_000; // 20 seconds

const AppUpdateToast: React.FC<AppUpdateProps> = ({ version, onUpdate }) => {
  const classes = useStyles();
  const inAgentConsole = window.location.pathname.startsWith(urlAgentConsole);

  const { formatMessage } = useIntl();
  // Draggable state
  const rootRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragStart = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return; // Don't drag when clicking buttons

    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    setIsDragging(true);
    e.preventDefault();
  };

  useEffect(() => {
    // eslint-disable-next-line consistent-return
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!rootRef.current) return;

      const rect = rootRef.current.getBoundingClientRect();
      const newX = e.clientX - dragStart.current.x;
      const newY = e.clientY - dragStart.current.y;

      // Calculate boundaries relative to top-center corner
      const minX = -window.innerWidth / 2 + rect.width / 2;
      const maxX = window.innerWidth / 2 - rect.width / 2;
      const minY = 0;
      const maxY = window.innerHeight - rect.height - 20; // 20px from bottom edge
      // Constrain position within viewport
      setPosition({
        x: Math.max(minX, Math.min(newX, maxX)),
        y: Math.max(minY, Math.min(newY, maxY)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const [time, setTime] = useState(DEFAULT_TIMEOUT);

  useEffect(() => {
    if (inAgentConsole) return undefined;

    const interval = setInterval(() => {
      setTime(prev => {
        if (prev <= 250) {
          clearInterval(interval);
          onUpdate();
          return 0;
        }
        return prev - 250;
      });
    }, 250);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progress = inAgentConsole
    ? 0
    : Math.floor((time / DEFAULT_TIMEOUT) * 100);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={rootRef}
      className={classes.root}
      role="status"
      aria-live="polite"
      aria-label={formatMessage({ id: 'AppUpdate.Toast.AriaLabel' })}
      tabIndex={-1}
      onMouseDown={handleMouseDown}
      style={{
        position: 'relative',
        left: position.x,
        top: position.y,
        zIndex: 10_000,
      }}
    >
      <div className={classes.shadow} />
      <div className={classes.dot} />
      <div className={classes.content}>
        <div className={classes.iconWrapper}>
          <BoltIcon fontSize="inherit" />
        </div>

        <div className={classes.textBlock}>
          <div className={classes.titleRow}>
            <Typography className={classes.title}>
              <FormattedMessage
                id="AppUpdate.Toast.Title"
                values={{ version }}
              />
            </Typography>
            <span className={classes.badge}>
              <FormattedMessage id="AppUpdate.Toast.Badge" />
            </span>
          </div>
          <Typography className={classes.subtitle}>
            <FormattedMessage id="AppUpdate.Toast.Subtitle" />
          </Typography>
        </div>

        <div className={classes.separator} />

        <button
          type="button"
          className={classes.updateButton}
          onClick={onUpdate}
          aria-label={formatMessage({
            id: 'AppUpdate.Toast.UpdateButton.AriaLabel',
          })}
        >
          <ArrowCircleUpOutlinedIcon fontSize="inherit" />
          <FormattedMessage id="AppUpdate.Toast.UpdateButton" />
          <ChevronRight className="chevron" fontSize="inherit" />
        </button>

        <LinearProgress
          className={classes.progress}
          variant="determinate"
          value={progress}
        />
      </div>
    </div>
  );
};

export default AppUpdateToast;

const useStyles = makeStyles(theme => ({
  '@keyframes glowPulse': {
    '0%, 100%': { boxShadow: '0 0 1.25rem rgba(168, 85, 247, 0.6)' },
    '50%': { boxShadow: '0 0 1.875rem rgba(59, 130, 246, 0.8)' },
  },
  '@keyframes float': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-0.25rem)' },
  },
  root: {
    position: 'relative',
    padding: '0.5rem',
    cursor: 'grab',
    userSelect: 'none',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  shadow: {
    position: 'absolute',
    inset: 0,
    borderRadius: 999,
    background:
      'linear-gradient(to right, rgba(168, 85, 247, 0.2), rgba(37, 99, 235, 0.2), rgba(29, 78, 216, 0.2))',
    filter: 'blur(0.5rem)',
    zIndex: -1,
  },
  content: {
    position: 'relative',
    background:
      theme.palette.type === 'dark'
        ? 'linear-gradient(to right, #111827, #1f2937, #111827)'
        : 'linear-gradient(to right, #f9fafb, #ffffff, #f9fafb)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.75rem',
    borderRadius: 999,
    padding: '1rem 1.5rem',
    overflow: 'hidden',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '2.5rem',
    width: '2.5rem',
    height: '2.5rem',
    fontSize: '1.25rem',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #a855f7, #2563eb)',
    animation: '$glowPulse 2s ease-in-out infinite',
    color: '#fff',
  },
  textBlock: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: '0.875rem',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
  },
  badge: {
    background:
      'linear-gradient(to right, rgba(168, 85, 247, 0.2), rgba(139, 92, 246, 0.2))',
    borderColor: 'rgba(168, 85, 247, 0.4)',
    border: '1px solid',
    color: '#d8b4fe',
    fontSize: '0.625rem',
    fontWeight: 600,
    borderRadius: '1.25rem',
    padding: '0.0625rem 0.5rem',
    lineHeight: 1.6,
  },
  subtitle: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
  },
  separator: {
    height: '2.5rem',
    width: '0.0625rem',
    background: 'linear-gradient(to bottom, transparent, #4b5563, transparent)',
  },
  updateButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    background: 'linear-gradient(to right, #a855f7, #2563eb)',
    color: '#fff',
    border: 'none',
    borderRadius: '1.25rem',
    padding: '0.5rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 0.625rem 0.9375rem -0.1875rem rgba(0, 0, 0, 0.3)',
    '&:hover': {
      background: 'linear-gradient(to right, #c084fc, #3b82f6)',
      boxShadow: '0 0.625rem 0.9375rem -0.1875rem rgba(168, 85, 247, 0.5)',
    },
    '& .chevron': {
      transition: 'transform 0.2s ease',
    },
    '&:hover .chevron': {
      transform: 'translateX(0.125rem)',
    },
  },
  progress: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '0.25rem',
    background: 'rgba(55, 65, 81, 0.5)',
    '& .MuiLinearProgress-bar': {
      background: 'linear-gradient(to right, #a855f7, #2563eb)',
    },
  },
  dot: {
    position: 'absolute',
    top: '-0.2rem',
    left: 'calc(50% - 0.1875rem)',
    animation: '$float 2s ease-in-out infinite',
    width: '0.375rem',
    height: '0.375rem',
    background:
      'linear-gradient(to right, rgb(168, 85, 247), rgb(37, 99, 235))',
    borderRadius: 999,
    boxShadow: '0 0.625rem 0.9375rem -0.1875rem rgba(168, 85, 247, 0.5)',
  },
}));
